import {
  Button,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import dayjs from 'dayjs';
import {
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import {
  convertEvaluationType,
  convertReportGoal,
  convertStatus,
  convertStatusUrl,
  DistributionStage,
  HiveRequestAcceptanceStatus,
  SlaStatus,
  Status,
} from '../../../../api/enumerations';
import { resolveBankDossier } from '../../../../api/workOrders/bankDossier';
import { WorkOrderData } from '../../../../api/workOrders/types';
import { DossierKpis } from '../../../../components/BankDossier';
import { EmptyState } from '../../../../components/UI/EmptyState';
import { TableSkeleton } from '../../../../components/UI/TableSkeleton';
import { Constants as DossierConstants } from '../../../../constants/bankDossier';
import { Constants } from '../../../../constants/home';
import {
  IconArrowDownwardMS,
  IconArrowUpwardMS,
  IconSearchMS,
} from '../../../../constants/icons';
import { isOfferingStage } from '../../../../helpers/distribution';
import { QuickFilters, QuickFilterKey } from './QuickFilters';
import { StatusChips } from './StatusChips';
import {
  HeaderCell,
  LoadMoreRow,
  StyledTable,
  TableScroll,
  TableWrapper,
  Toolbar,
} from './styles';
import { WorkOrderRow } from './WorkOrderRow';

interface TableViewProps {
  data: WorkOrderData[];
  loading: boolean;
  /** Server has more pages beyond `data` (paged flat list). */
  hasMore?: boolean;
  loadingMore?: boolean;
  onLoadMore?: () => void;
}

type SortKey =
  | 'reference_number'
  | 'client_name'
  | 'status'
  | 'company'
  | 'evaluation'
  | 'goal'
  | 'schedule'
  | 'limit_finish'
  | 'control'
  | 'appraisal';

interface ColumnDef {
  key: SortKey | 'inspector' | 'location' | 'actions';
  label: string;
  sortable: boolean;
  width?: string;
}

// Sentence case headers (guide 7.5).
const COLUMNS: ColumnDef[] = [
  { key: 'reference_number', label: 'OS', sortable: true, width: '88px' },
  { key: 'client_name', label: 'Cliente', sortable: true, width: '240px' },
  {
    key: 'control',
    label: DossierConstants.list.columnControl,
    sortable: true,
    width: '170px',
  },
  { key: 'status', label: 'Etapa', sortable: true, width: '150px' },
  { key: 'company', label: 'Empresa', sortable: true, width: '180px' },
  { key: 'inspector', label: 'Vistoriador', sortable: false, width: '160px' },
  { key: 'location', label: 'Localização', sortable: false, width: '180px' },
  { key: 'evaluation', label: 'Modalidade', sortable: true, width: '160px' },
  { key: 'goal', label: 'Objetivo', sortable: true, width: '180px' },
  { key: 'schedule', label: 'Agendamento', sortable: true, width: '120px' },
  { key: 'limit_finish', label: 'Entrega', sortable: true, width: '200px' },
  {
    key: 'appraisal',
    label: DossierConstants.list.columnAppraisal,
    sortable: true,
    width: '150px',
  },
];

const PAGE_STEP = 50;

const matchesQuickFilter = (
  row: WorkOrderData,
  filter: QuickFilterKey | null
): boolean => {
  if (!filter) return true;
  switch (filter) {
    case 'overdue':
      return row.sla_status === SlaStatus.OVERDUE;
    case 'near_deadline':
      return row.sla_status === SlaStatus.NEAR_DEADLINE;
    case 'no_inspector':
      return !row.inspection?.engineer_name;
    case 'scheduled_today': {
      const date = row.inspection?.schedule_at;
      if (!date) return false;
      return dayjs(date).isSame(dayjs(), 'day');
    }
    case 'rejected_by_bank':
      return (
        row.hive_request?.acceptance_status ===
        HiveRequestAcceptanceStatus.REJECTED
      );
    case 'in_offer':
      return isOfferingStage(row.distribution_stage);
    case 'manual_required':
      return row.distribution_stage === DistributionStage.MANUAL_REQUIRED;
    default:
      return true;
  }
};

interface AllCounters {
  status: Partial<Record<Status, number>>;
  quick: Partial<Record<QuickFilterKey, number>>;
}

const buildCounters = (rows: WorkOrderData[]): AllCounters => {
  const status: Partial<Record<Status, number>> = {};
  const quick: Partial<Record<QuickFilterKey, number>> = {
    overdue: 0,
    near_deadline: 0,
    scheduled_today: 0,
    no_inspector: 0,
    rejected_by_bank: 0,
    in_offer: 0,
    manual_required: 0,
  };
  const todayStart = dayjs().startOf('day');
  rows.forEach((row) => {
    status[row.status] = (status[row.status] ?? 0) + 1;
    if (row.sla_status === SlaStatus.OVERDUE) {
      quick.overdue = (quick.overdue ?? 0) + 1;
    }
    if (row.sla_status === SlaStatus.NEAR_DEADLINE) {
      quick.near_deadline = (quick.near_deadline ?? 0) + 1;
    }
    if (!row.inspection?.engineer_name) {
      quick.no_inspector = (quick.no_inspector ?? 0) + 1;
    }
    if (
      row.inspection?.schedule_at &&
      dayjs(row.inspection.schedule_at).isSame(todayStart, 'day')
    ) {
      quick.scheduled_today = (quick.scheduled_today ?? 0) + 1;
    }
    if (
      row.hive_request?.acceptance_status ===
      HiveRequestAcceptanceStatus.REJECTED
    ) {
      quick.rejected_by_bank = (quick.rejected_by_bank ?? 0) + 1;
    }
    if (isOfferingStage(row.distribution_stage)) {
      quick.in_offer = (quick.in_offer ?? 0) + 1;
    }
    if (row.distribution_stage === DistributionStage.MANUAL_REQUIRED) {
      quick.manual_required = (quick.manual_required ?? 0) + 1;
    }
  });
  return { status, quick };
};

const sortValueFor = (row: WorkOrderData, key: SortKey): string | number => {
  switch (key) {
    case 'reference_number':
      return row.reference_number;
    case 'client_name':
      return row.client_name?.toLowerCase() ?? '';
    case 'status':
      return convertStatus(row.status);
    case 'company':
      return row.worker_company?.name?.toLowerCase() ?? '';
    case 'evaluation':
      return (
        row.report_type_label || convertEvaluationType(row.evaluation_type)
      );
    case 'goal':
      return convertReportGoal(row.report_goal);
    case 'schedule':
      return row.inspection?.schedule_at
        ? dayjs(row.inspection.schedule_at).valueOf()
        : Number.MAX_SAFE_INTEGER;
    case 'limit_finish':
      // The cell shows date + SLA derived from sla_days_remaining; sorting by
      // limit_finish_at is useless because the field arrives empty for most
      // OS. sla_days_remaining carries the urgency (negative = overdue).
      if (typeof row.sla_days_remaining === 'number') {
        return row.sla_days_remaining;
      }
      return Number.MAX_SAFE_INTEGER;
    case 'control':
      return resolveBankDossier(row)?.identification.bank_control_number ?? '';
    case 'appraisal':
      return resolveBankDossier(row)?.appraisal_value ?? -1;
    default:
      return '';
  }
};

const ariaSortFor = (
  isActive: boolean,
  direction: 'asc' | 'desc'
): 'ascending' | 'descending' | undefined => {
  if (!isActive) return undefined;
  return direction === 'asc' ? 'ascending' : 'descending';
};

export function TableView({
  data,
  loading,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
}: TableViewProps): JSX.Element {
  const navigate = useNavigate();
  const [sortKey, setSortKey] = useState<SortKey>('limit_finish');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [quickFilter, setQuickFilter] = useState<QuickFilterKey | null>(null);
  const [statusFilter, setStatusFilter] = useState<Status[]>([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_STEP);

  const counters = useMemo(() => buildCounters(data), [data]);

  const filtered = useMemo(
    () =>
      data.filter((row) => {
        if (statusFilter.length > 0 && !statusFilter.includes(row.status)) {
          return false;
        }
        return matchesQuickFilter(row, quickFilter);
      }),
    [data, quickFilter, statusFilter]
  );

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => {
      const av = sortValueFor(a, sortKey);
      const bv = sortValueFor(b, sortKey);
      if (av < bv) return sortDirection === 'asc' ? -1 : 1;
      if (av > bv) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [filtered, sortKey, sortDirection]);

  // "Carregar mais" instead of numbered pages (guide 7.5).
  useEffect(() => {
    setVisibleCount(PAGE_STEP);
  }, [quickFilter, statusFilter, sortKey, sortDirection]);

  const visible = sorted.slice(0, visibleCount);
  const remaining = Math.max(sorted.length - visible.length, 0);

  const toggleSort = (key: SortKey): void => {
    if (sortKey === key) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const handleSortKeyDown = (
    e: KeyboardEvent<HTMLTableCellElement>,
    key: SortKey
  ): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleSort(key);
    }
  };

  const navigateToRow = useCallback(
    (row: WorkOrderData): void => {
      navigate(`/home/property/${row.id}/${convertStatusUrl(row.status)}`);
    },
    [navigate]
  );

  const countLabel = (): string => {
    if (loading) return 'Carregando ordens de serviço';
    const total = sorted.length;
    const noun = total === 1 ? 'ordem de serviço' : 'ordens de serviço';
    const suffix = quickFilter || statusFilter.length > 0 ? ' filtradas' : '';
    return `${total} ${noun}${suffix}`;
  };

  const showSkeleton = loading && data.length === 0;
  const showEmpty = !loading && visible.length === 0;

  return (
    <TableWrapper>
      <DossierKpis rows={filtered} loading={showSkeleton} />
      <StatusChips
        selected={statusFilter}
        onChange={setStatusFilter}
        counters={counters.status}
      />
      <QuickFilters
        active={quickFilter}
        onChange={setQuickFilter}
        counters={counters.quick}
      />
      <Toolbar>
        <span aria-live="polite">{countLabel()}</span>
      </Toolbar>
      <TableScroll>
        <TableContainer>
          <StyledTable size="small">
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => {
                  const isSortable = col.sortable;
                  const isActive = isSortable && sortKey === col.key;
                  return (
                    <HeaderCell
                      key={col.key}
                      sortable={isSortable}
                      className={isActive ? 'active' : undefined}
                      style={{ width: col.width }}
                      role={isSortable ? 'columnheader button' : 'columnheader'}
                      tabIndex={isSortable ? 0 : -1}
                      aria-sort={ariaSortFor(isActive, sortDirection)}
                      onClick={
                        isSortable
                          ? () => toggleSort(col.key as SortKey)
                          : undefined
                      }
                      onKeyDown={
                        isSortable
                          ? (e) => handleSortKeyDown(e, col.key as SortKey)
                          : undefined
                      }
                    >
                      {col.label}
                      {isActive && (
                        <span className="sort-icon" aria-hidden="true">
                          {sortDirection === 'asc'
                            ? IconArrowUpwardMS
                            : IconArrowDownwardMS}
                        </span>
                      )}
                    </HeaderCell>
                  );
                })}
              </TableRow>
            </TableHead>
            {showSkeleton ? (
              <TableSkeleton columns={COLUMNS.length} />
            ) : (
              <TableBody>
                {showEmpty && (
                  <TableRow>
                    <TableCell colSpan={COLUMNS.length} sx={{ height: 'auto' }}>
                      <EmptyState
                        icon={IconSearchMS}
                        message={Constants.tableEmpty}
                        action={
                          quickFilter || statusFilter.length > 0 ? (
                            <Button
                              variant="outlined"
                              onClick={() => {
                                setQuickFilter(null);
                                setStatusFilter([]);
                              }}
                            >
                              {Constants.clearFilters}
                            </Button>
                          ) : undefined
                        }
                      />
                    </TableCell>
                  </TableRow>
                )}
                {visible.map((row) => (
                  <WorkOrderRow key={row.id} row={row} onOpen={navigateToRow} />
                ))}
              </TableBody>
            )}
          </StyledTable>
        </TableContainer>
        {(remaining > 0 || hasMore) && (
          <LoadMoreRow>
            <span>
              Mostrando {visible.length} de {sorted.length}
              {hasMore ? '+' : ''}
            </span>
            <Button
              variant="outlined"
              size="small"
              disabled={loadingMore}
              onClick={() => {
                if (remaining > 0) {
                  setVisibleCount((count) => count + PAGE_STEP);
                } else {
                  onLoadMore?.();
                }
              }}
            >
              {loadingMore ? 'Carregando…' : Constants.loadMore}
            </Button>
          </LoadMoreRow>
        )}
      </TableScroll>
    </TableWrapper>
  );
}
